import { useRef, useState } from 'react';
import type { Book } from '@booknest/shared';

import { Shelf } from './Shelf.js';
import type { BookcaseView } from './Bookcase.js';
import type { Density } from './spine-utils.js';
import { useShelfCapacity } from './use-shelf-capacity.js';

export interface BookcaseUnitData {
  readonly key: string;
  readonly label: string;
  readonly emoji?: string;
  readonly books: ReadonlyArray<Book>;
  readonly emptyMessage?: string;
  /** Force le premier livre à être affiché face avant. */
  readonly firstAsCover?: boolean;
}

interface BookcaseUnitProps extends BookcaseUnitData {
  view?: BookcaseView;
  /** Sert au calcul de capacité par rangée — les dimensions visuelles passent par CSS vars. */
  density?: Density;
  /**
   * Nombre de livres "cible" — sert à aligner ce meuble sur le plus grand de
   * sa grille. On padde avec des rangées vides jusqu'à atteindre le même
   * nombre de planches que le meuble cible. Par défaut : la longueur propre
   * du meuble (pas de padding).
   */
  matchToBookCount?: number;
  openingBookId?: string | null;
  onBookClick?: (book: Book) => void;
}

/**
 * Nombre maximum de rangées (étagères) empilées dans un même meuble.
 * Recette maquette §11 : 4 rangées de 30 livres = un meuble "tome" complet.
 * Au-delà, on pagine entre "tome 1/N", "tome 2/N", etc.
 */
const MAX_SHELVES_PER_UNIT = 4;

/**
 * Un meuble bibliothèque — empile jusqu'à `MAX_SHELVES_PER_UNIT` rangées de
 * livres. La capacité par rangée vient de `useShelfCapacity` (basé sur la
 * largeur réelle observée + densité). Si la collection dépasse, pagination
 * tome via les boutons ‹ / ›.
 */
export function BookcaseUnit({
  label,
  emoji,
  books,
  emptyMessage = 'Étagère vide pour le moment…',
  firstAsCover = false,
  view = 'flat',
  density = 'comfy',
  matchToBookCount,
  openingBookId = null,
  onBookClick,
}: BookcaseUnitProps): JSX.Element {
  const bookcaseRef = useRef<HTMLDivElement>(null);
  const capacity = useShelfCapacity(bookcaseRef, density);
  const [tome, setTome] = useState(0);

  const perTome = Math.max(1, capacity * MAX_SHELVES_PER_UNIT);
  const totalTomes = Math.max(1, Math.ceil(books.length / perTome));
  const safeTome = Math.min(tome, totalTomes - 1);
  const tomeBooks = books.slice(safeTome * perTome, (safeTome + 1) * perTome);

  // Split tomeBooks across multiple shelf rows.
  const rows: ReadonlyArray<ReadonlyArray<Book>> = (() => {
    if (tomeBooks.length === 0) return [];
    const out: Book[][] = [];
    for (let i = 0; i < tomeBooks.length; i += capacity) {
      out.push([...tomeBooks.slice(i, i + capacity)]);
    }
    return out;
  })();

  const goPrev = (): void => setTome((t) => Math.max(0, t - 1));
  const goNext = (): void => setTome((t) => Math.min(totalTomes - 1, t + 1));

  return (
    <div className="bookcase-unit">
      <div className="bookcase-unit-header">
        <h3 className="font-serif text-base font-semibold text-brown">
          {emoji && <span className="mr-1.5" aria-hidden>{emoji}</span>}
          {label}
          {totalTomes > 1 && (
            <span className="ml-2 font-sans text-xs font-normal text-brown-light">
              · tome {safeTome + 1}/{totalTomes}
            </span>
          )}
        </h3>
        <span className="font-script text-base text-brown-soft">
          {books.length === 0
            ? 'vide'
            : `${books.length} livre${books.length > 1 ? 's' : ''}`}
        </span>
      </div>
      <div className={`bookcase-wrap${view === '3d' ? ' view-3d' : ''}`}>
        <div className="bookcase" ref={bookcaseRef}>
          {(() => {
            // Nb de rangées requises pour le meuble "cible" — sert à aligner
            // visuellement plusieurs meubles dans la même grille sur la
            // catégorie qui a le plus de livres. Limité au tome courant et
            // au max de rangées par tome.
            const targetCount = Math.min(
              matchToBookCount ?? books.length,
              perTome,
            );
            const targetRows = Math.max(
              1,
              Math.min(
                MAX_SHELVES_PER_UNIT,
                Math.ceil(targetCount / Math.max(1, capacity)),
              ),
            );

            if (books.length === 0) {
              return (
                <>
                  <div className="bookshelf bookshelf-empty-wrapper">
                    <p className="bookshelf-empty">{emptyMessage}</p>
                  </div>
                  {Array.from({ length: Math.max(0, targetRows - 1) }).map((_, idx) => (
                    <Shelf
                      key={`pad-${idx}`}
                      books={[]}
                      shelfIndex={idx + 1}
                      silentWhenEmpty
                    />
                  ))}
                </>
              );
            }

            const padCount = Math.max(0, targetRows - rows.length);
            return (
              <>
                {rows.map((row, idx) => (
                  <Shelf
                    key={idx}
                    books={row}
                    shelfIndex={idx}
                    firstAsCover={firstAsCover && safeTome === 0 && idx === 0}
                    openingBookId={openingBookId}
                    onBookClick={onBookClick}
                  />
                ))}
                {Array.from({ length: padCount }).map((_, idx) => (
                  <Shelf
                    key={`pad-${idx}`}
                    books={[]}
                    shelfIndex={rows.length + idx}
                    silentWhenEmpty
                  />
                ))}
              </>
            );
          })()}
          {totalTomes > 1 && (
            <div className="shelf-meta">
              <button
                type="button"
                aria-label="Tome précédent"
                onClick={goPrev}
                disabled={safeTome === 0}
              >
                ‹
              </button>
              <span>
                Tome {safeTome + 1} / {totalTomes}
              </span>
              <button
                type="button"
                aria-label="Tome suivant"
                onClick={goNext}
                disabled={safeTome >= totalTomes - 1}
              >
                ›
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
